import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceTemplateService, InvoiceTemplate, CreateTemplateData } from '../services/invoiceTemplateService';
import { toast } from 'react-hot-toast';

const ds = {
  bg: '#faf7ee',
  card: '#ffffff',
  elevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#fbbf24',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  borderNormal: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  display: 'block',
  marginBottom: 6,
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: ds.textSecondary,
  border: `1px solid ${ds.borderSubtle}`,
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

export const InvoiceTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InvoiceTemplate | null>(null);

  const [formData, setFormData] = useState<CreateTemplateData>({
    name: '',
    description: '',
    templateCompanyName: '',
    layoutType: 'modern',
    fontFamily: 'Helvetica',
    isDefault: false,
  });

  // Fetch templates
  const { data: templates, isLoading } = useQuery<InvoiceTemplate[]>({
    queryKey: ['invoiceTemplates'],
    queryFn: () => invoiceTemplateService.getAll(false),
  });

  // Create template mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTemplateData) => invoiceTemplateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      toast.success('Template created successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create template');
    },
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      invoiceTemplateService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      toast.success('Template updated successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update template');
    },
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => invoiceTemplateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      toast.success('Template deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete template');
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      invoiceTemplateService.uploadLogo(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      toast.success('Logo uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload logo');
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => invoiceTemplateService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      toast.success('Default template set successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to set default template');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateCompanyName: '',
      layoutType: 'modern',
      fontFamily: 'Helvetica',
      isDefault: false,
    });
    setIsCreating(false);
    setEditingTemplate(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (template: InvoiceTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      templateCompanyName: template.templateCompanyName,
      layoutType: template.layoutType,
      colorScheme: template.colorScheme,
      fontFamily: template.fontFamily,
      isDefault: template.isDefault,
    });
    setIsCreating(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete the template "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, templateId: string) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      uploadLogoMutation.mutate({ id: templateId, file });
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}>
        <span style={{ fontFamily: ds.fontBody, fontSize: 13, color: ds.textMuted }}>
          Loading templates...
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, padding: 28 }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{
            fontFamily: ds.fontHeading,
            fontSize: 28,
            fontWeight: 400,
            color: ds.textPrimary,
            margin: 0,
          }}>
            Invoice Templates
          </h1>
          <p style={{
            fontFamily: ds.fontBody,
            fontSize: 12,
            color: ds.textMuted,
            margin: '6px 0 0',
          }}>
            Manage invoice templates with different company names and logos
          </p>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            style={primaryButtonStyle}
          >
            + New Template
          </button>
        )}
      </div>

      {/* Create/Edit Form */}
      {isCreating && (
        <div style={{
          background: ds.card,
          border: `1px solid ${ds.borderSubtle}`,
          borderRadius: 8,
          padding: 24,
        }}>
          <h2 style={{
            fontFamily: ds.fontHeading,
            fontSize: 20,
            fontWeight: 400,
            color: ds.textPrimary,
            margin: '0 0 20px',
          }}>
            {editingTemplate ? 'Edit Template' : 'Create New Template'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 16,
            }}>
              <div>
                <label style={labelStyle}>
                  Template Name <span style={{ color: '#c0504a' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  Company Name <span style={{ color: '#c0504a' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.templateCompanyName}
                  onChange={(e) =>
                    setFormData({ ...formData, templateCompanyName: e.target.value })
                  }
                  required
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Layout Type</label>
                <select
                  value={formData.layoutType}
                  onChange={(e) => {
                    const newLayoutType = e.target.value as 'modern' | 'classic';
                    const defaultColors = invoiceTemplateService.getDefaultColorScheme(newLayoutType);
                    setFormData({
                      ...formData,
                      layoutType: newLayoutType,
                      colorScheme: defaultColors,
                    });
                  }}
                  style={inputStyle}
                >
                  <option value="modern">Modern (Blue theme)</option>
                  <option value="classic">Classic (Gray theme)</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Font Family</label>
                <select
                  value={formData.fontFamily}
                  onChange={(e) =>
                    setFormData({ ...formData, fontFamily: e.target.value })
                  }
                  style={inputStyle}
                >
                  <option value="Helvetica">Helvetica</option>
                  <option value="Times-Roman">Times New Roman</option>
                  <option value="Courier">Courier</option>
                  <option value="Arial">Arial</option>
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Color Customization</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 16,
                }}>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 8 }}>Primary Color</label>
                    <input
                      type="color"
                      value={formData.colorScheme?.primary || '#2563EB'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          colorScheme: { ...formData.colorScheme!, primary: e.target.value },
                        })
                      }
                      style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${ds.borderSubtle}`,
                        borderRadius: 4,
                        background: '#f4efe2',
                        cursor: 'pointer',
                        padding: 2,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 8 }}>Secondary Color</label>
                    <input
                      type="color"
                      value={formData.colorScheme?.secondary || '#10B981'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          colorScheme: { ...formData.colorScheme!, secondary: e.target.value },
                        })
                      }
                      style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${ds.borderSubtle}`,
                        borderRadius: 4,
                        background: '#f4efe2',
                        cursor: 'pointer',
                        padding: 2,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 8 }}>Text Color</label>
                    <input
                      type="color"
                      value={formData.colorScheme?.text || '#1F2937'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          colorScheme: { ...formData.colorScheme!, text: e.target.value },
                        })
                      }
                      style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${ds.borderSubtle}`,
                        borderRadius: 4,
                        background: '#f4efe2',
                        cursor: 'pointer',
                        padding: 2,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 8 }}>Background Color</label>
                    <input
                      type="color"
                      value={formData.colorScheme?.background || '#FFFFFF'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          colorScheme: { ...formData.colorScheme!, background: e.target.value },
                        })
                      }
                      style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${ds.borderSubtle}`,
                        borderRadius: 4,
                        background: '#f4efe2',
                        cursor: 'pointer',
                        padding: 2,
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  style={{
                    ...inputStyle,
                    resize: 'vertical',
                    fontFamily: ds.fontBody,
                  }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    style={{
                      width: 14,
                      height: 14,
                      accentColor: ds.gold,
                      cursor: 'pointer',
                    }}
                  />
                  <span style={{
                    fontFamily: ds.fontBody,
                    fontSize: 12,
                    color: ds.textSecondary,
                  }}>
                    Set as default template
                  </span>
                </label>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              paddingTop: 20,
              marginTop: 20,
              borderTop: `1px solid ${ds.borderNormal}`,
            }}>
              <button
                type="button"
                onClick={resetForm}
                style={ghostButtonStyle}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  opacity: (createMutation.isPending || updateMutation.isPending) ? 0.6 : 1,
                  cursor: (createMutation.isPending || updateMutation.isPending) ? 'not-allowed' : 'pointer',
                }}
              >
                {editingTemplate ? 'Update' : 'Create'} Template
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 24,
      }}>
        {templates?.map((template) => (
          <div
            key={template.id}
            style={{
              background: ds.card,
              border: `1px solid ${ds.borderNormal}`,
              borderRadius: 8,
              padding: 24,
              position: 'relative',
            }}
          >
            {template.isDefault && (
              <span style={{
                position: 'absolute',
                top: 10,
                right: 10,
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: 20,
                background: `rgba(92,78,51,0.25)`,
                color: ds.gold,
                fontFamily: ds.fontLabel,
                fontSize: 8,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}>
                Default
              </span>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ flexShrink: 0 }}>
                {template.logoBase64 ? (
                  <img
                    src={template.logoBase64}
                    alt="Template Logo"
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: 'contain',
                      border: `1px solid ${ds.borderSubtle}`,
                      borderRadius: 4,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 64,
                    height: 64,
                    border: `2px dashed ${ds.borderSubtle}`,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <svg width={28} height={28} fill="none" stroke={ds.textMuted} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  fontFamily: ds.fontHeading,
                  fontSize: 18,
                  fontWeight: 400,
                  color: ds.textPrimary,
                  margin: 0,
                }}>
                  {template.name}
                </h3>
                <p style={{
                  fontFamily: ds.fontBody,
                  fontSize: 12,
                  color: ds.textSecondary,
                  margin: '3px 0 0',
                }}>
                  {template.templateCompanyName}
                </p>
                <p style={{
                  fontFamily: ds.fontLabel,
                  fontSize: 8,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: ds.textMuted,
                  margin: '5px 0 0',
                }}>
                  {template.layoutType === 'modern' ? 'Modern Layout' : 'Classic Layout'}
                </p>
              </div>
            </div>

            {template.description && (
              <p style={{
                fontFamily: ds.fontBody,
                fontSize: 12,
                color: ds.textSecondary,
                margin: '14px 0 0',
              }}>
                {template.description}
              </p>
            )}

            <div style={{
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <label style={{ flex: 1, cursor: 'pointer' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, template.id)}
                  style={{ display: 'none' }}
                />
                <span style={{
                  display: 'inline-block',
                  padding: '6px 12px',
                  border: `1px solid ${ds.borderSubtle}`,
                  borderRadius: 4,
                  fontFamily: ds.fontLabel,
                  fontSize: 8,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: ds.textSecondary,
                  cursor: 'pointer',
                }}>
                  {template.templateLogo ? 'Change Logo' : 'Upload Logo'}
                </span>
              </label>

              <button
                onClick={() => handleEdit(template)}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: `1px solid ${ds.borderSubtle}`,
                  borderRadius: 4,
                  fontFamily: ds.fontLabel,
                  fontSize: 8,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: ds.gold,
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>

              {!template.isDefault && (
                <button
                  onClick={() => setDefaultMutation.mutate(template.id)}
                  title="Set as default"
                  style={{
                    padding: '6px 10px',
                    background: 'transparent',
                    border: `1px solid ${ds.borderSubtle}`,
                    borderRadius: 4,
                    fontFamily: ds.fontBody,
                    fontSize: 13,
                    color: ds.textMuted,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  ★
                </button>
              )}

              <button
                onClick={() => handleDelete(template.id, template.name)}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid rgba(192,80,74,0.3)',
                  borderRadius: 4,
                  fontFamily: ds.fontLabel,
                  fontSize: 8,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#c0504a',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {templates?.length === 0 && !isCreating && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px 0',
          gap: 12,
        }}>
          <svg
            width={48}
            height={48}
            fill="none"
            stroke={ds.textMuted}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 style={{
            fontFamily: ds.fontHeading,
            fontSize: 20,
            fontWeight: 400,
            color: ds.textSecondary,
            margin: 0,
          }}>
            No templates
          </h3>
          <p style={{
            fontFamily: ds.fontBody,
            fontSize: 12,
            color: ds.textMuted,
            margin: 0,
          }}>
            Get started by creating a new template.
          </p>
        </div>
      )}
    </div>
  );
};
